import type { AuthProviderId, B2cConfig } from "./authProviders";

/**
 * Azure AD B2C ROPC (Resource Owner Password Credentials) client.
 *
 * Mirrors Provider Portal's client-side pattern: POSTs email/password to the
 * B2C token endpoint and receives an access + refresh token. Only works
 * against a B2C user flow that has ROPC enabled (typically a dedicated
 * `b2c_1a_ropc_*` policy).
 *
 * Tokens are cached in localStorage namespaced by provider so the two B2C
 * providers can coexist without stomping each other.
 */

export interface B2cTokens {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_at: number; // epoch seconds, computed at store time
  token_type: string;
  /**
   * The email this record was AUTHENTICATED with (normalized: trim +
   * lowercase), set by `ropcLogin` from its own `email` argument — never
   * decoded out of `id_token`. Claim-shape-independent, so it holds even
   * against a B2C policy that emits no email claim at all: the Provider Portal
   * ROPC policy emits only `sub` and a `name` carrying an opaque PortalManager
   * Key ID, which is why decoding alone couldn't produce a usable identity for
   * a PP session (AM-5930). `ropcRefresh` carries the existing value forward —
   * a refresh never changes who the token belongs to. Absent on a record
   * stored before this existed; callers must treat it as optional.
   */
  boundIdentityEmail?: string;
}

interface B2cTokenResponse {
  access_token: string;
  refresh_token?: string;
  id_token?: string;
  expires_in: number;
  token_type: string;
}

const STORAGE_PREFIX = "member-360:b2c-tokens:";

function storageKey(id: AuthProviderId) {
  return `${STORAGE_PREFIX}${id}`;
}

export function getStoredTokens(id: AuthProviderId): B2cTokens | null {
  try {
    const raw = window.localStorage.getItem(storageKey(id));
    return raw ? (JSON.parse(raw) as B2cTokens) : null;
  } catch {
    return null;
  }
}

export function setStoredTokens(id: AuthProviderId, tokens: B2cTokens): void {
  try {
    window.localStorage.setItem(storageKey(id), JSON.stringify(tokens));
  } catch {
    /* noop */
  }
}

export function clearStoredTokens(id: AuthProviderId): void {
  try {
    window.localStorage.removeItem(storageKey(id));
  } catch {
    /* noop */
  }
}

function tokenEndpoint(cfg: B2cConfig): string {
  return `https://${cfg.domainName}.b2clogin.com/${cfg.domainName}.onmicrosoft.com/${cfg.userFlow}/oauth2/v2.0/token`;
}

function toTokens(res: B2cTokenResponse): B2cTokens {
  return {
    access_token: res.access_token,
    refresh_token: res.refresh_token,
    id_token: res.id_token,
    expires_at: Math.floor(Date.now() / 1000) + res.expires_in,
    token_type: res.token_type,
  };
}

/**
 * Extracts the B2C error message from a failed token response. B2C returns
 * `{ error_description: "AADB2CXXXXXX: ..." }` — the AADB2C prefix is
 * noisy and the remaining text is often still user-hostile
 * (`The username or password provided in the request are invalid.`), so
 * we also map the few codes a real user might hit to friendlier
 * copy. Codes not in the map fall back to the stripped description.
 */
const AADB2C_FRIENDLY_MESSAGES: Record<string, string> = {
  // Wrong username or password for ROPC flow
  AADB2C90225: "Incorrect email or password. Please try again.",
  AADB2C99002: "Incorrect email or password. Please try again.",
  // Account is locked after too many failed attempts
  AADB2C90149: "Too many failed attempts. Please wait a few minutes and try again.",
  AADB2C90157: "Too many failed attempts. Please wait a few minutes and try again.",
  // User not found / doesn't exist in the tenant
  AADB2C90024: "No account matches that email. Contact your administrator if this is wrong.",
  AADB2C99005: "No account matches that email. Contact your administrator if this is wrong.",
};

async function parseError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error_description?: string; error?: string };
    const raw = body.error_description ?? body.error ?? res.statusText;
    const codeMatch = raw.match(/^AADB2C(\d+):/);
    if (codeMatch) {
      const code = `AADB2C${codeMatch[1]}`;
      const mapped = AADB2C_FRIENDLY_MESSAGES[code];
      if (mapped) return mapped;
    }
    // Unknown code or no code at all — strip the AADB2C prefix and take
    // the first line, which is usually the short human-readable part.
    return raw.replace(/^AADB2C\d+:\s*/, "").split("\n")[0] ?? raw;
  } catch {
    return res.statusText || `HTTP ${res.status}`;
  }
}

export async function ropcLogin(
  cfg: B2cConfig,
  email: string,
  password: string,
): Promise<B2cTokens> {
  const params = new URLSearchParams({
    grant_type: "password",
    client_id: cfg.clientId,
    username: email,
    password,
    response_type: "token id_token",
    scope: `openid ${cfg.clientId} offline_access`,
  });

  const res = await fetch(tokenEndpoint(cfg), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body: params.toString(),
  });

  if (!res.ok) {
    throw new Error(await parseError(res));
  }

  const body = (await res.json()) as B2cTokenResponse;
  // Bind the record to the email actually authenticated with, rather than
  // relying on the id_token to carry one back (see B2cTokens.boundIdentityEmail
  // — the PP policy doesn't).
  const tokens: B2cTokens = {
    ...toTokens(body),
    boundIdentityEmail: email.trim().toLowerCase(),
  };
  setStoredTokens(cfg.id, tokens);
  return tokens;
}

export async function ropcRefresh(cfg: B2cConfig, refreshToken: string): Promise<B2cTokens> {
  const params = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: cfg.clientId,
    refresh_token: refreshToken,
    scope: `openid ${cfg.clientId} offline_access`,
  });

  const res = await fetch(tokenEndpoint(cfg), {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body: params.toString(),
  });

  if (!res.ok) {
    throw new Error(await parseError(res));
  }

  const body = (await res.json()) as B2cTokenResponse;
  // A refresh never changes who the token belongs to, so carry the existing
  // binding forward. Nothing to carry for a record stored before it existed.
  const previousBoundEmail = getStoredTokens(cfg.id)?.boundIdentityEmail;
  const tokens: B2cTokens = {
    ...toTokens(body),
    ...(previousBoundEmail ? { boundIdentityEmail: previousBoundEmail } : {}),
  };
  setStoredTokens(cfg.id, tokens);
  return tokens;
}

/**
 * True only for a value that is actually an email address — keeps an opaque
 * identifier out of a slot every caller treats as an email. Deliberately loose
 * about the local part (B2C accepts far more than RFC-common characters) and
 * strict only about the structure that separates an email from an id: exactly
 * one `@`, no whitespace, and a dotted domain.
 */
function looksLikeEmail(value: string | undefined): value is string {
  return !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Decodes an id_token's best-available email claim (normalized: trim +
 * lowercase), or `null` if it carries none. B2C policies disagree about which
 * claim holds the email, so this tries every one that might — but each
 * candidate must pass `looksLikeEmail`, so a claim holding something that
 * ISN'T an email is skipped rather than returned.
 *
 * That filter is the AM-5930 fix: Provider Portal's policy emits neither
 * `emails` nor `preferred_username`, so the chain used to fall through to
 * `name` — which under that policy carries an opaque PortalManager Key ID —
 * and that id became the account label.
 */
export function readB2cIdentityEmail(idToken?: string): string | null {
  if (!idToken) return null;
  try {
    const payload = idToken.split(".")[1];
    if (!payload) return null;
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, "+").replace(/_/g, "/")),
    ) as {
      emails?: string[];
      preferred_username?: string;
      email?: string;
      name?: string;
      upn?: string;
      signInName?: string;
    };
    const candidate = [
      decoded.emails?.[0],
      decoded.preferred_username,
      decoded.email,
      decoded.signInName,
      decoded.upn,
      decoded.name,
    ].find(looksLikeEmail);
    return candidate ? candidate.trim().toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * The account label for a B2C session (AuthContext's `accountLabel`) — the
 * user's email, or `null` when none can be established.
 *
 * Order matters (AM-5930): `boundIdentityEmail` is the email the session was
 * literally AUTHENTICATED with, so it holds for every policy shape, whereas the
 * id_token is only as good as the claims its policy emits — and the Provider
 * Portal ROPC policy emits none. Decoding first meant PP sessions fell through
 * to the `name` claim, which carries an opaque PortalManager Key ID, and that
 * id became the label. The decoder stays as the fallback for a session stored
 * before the binding existed (`ropcRefresh` can't invent one it never had), and
 * no longer returns a non-email claim, so the worst case is `null` — never an
 * id presented as a username.
 *
 * A null label is safe in this console: it renders no identity in its chrome
 * and its Sign out control is an unconditional sidebar button
 * (admin/components/AdminLayout.tsx), so nothing is hidden by it — the
 * session-expiry email pre-fill simply starts empty instead of seeded with an
 * id. Mirrors `readB2cAccountLabel` in member-360-standalone; keep the two in
 * step (this pair of copies is how AM-5930 came to exist in both).
 */
export function readB2cAccountLabel(
  tokens: B2cTokens | null | undefined,
): string | null {
  if (!tokens) return null;
  return tokens.boundIdentityEmail ?? readB2cIdentityEmail(tokens.id_token);
}

/** True if the access token expires in <60s (or is already expired). */
export function isExpiringSoon(tokens: B2cTokens, skewSeconds = 60): boolean {
  return tokens.expires_at - skewSeconds <= Math.floor(Date.now() / 1000);
}

/**
 * Returns a non-expired access token, refreshing first if needed. Returns
 * null if there's no session or the refresh fails (caller should show login).
 */
export async function getFreshAccessToken(
  cfg: B2cConfig,
): Promise<string | null> {
  const current = getStoredTokens(cfg.id);
  if (!current) return null;
  if (!isExpiringSoon(current)) return current.access_token;
  if (!current.refresh_token) {
    clearStoredTokens(cfg.id);
    return null;
  }
  try {
    const refreshed = await ropcRefresh(cfg, current.refresh_token);
    return refreshed.access_token;
  } catch {
    clearStoredTokens(cfg.id);
    return null;
  }
}
